import React, { useState } from 'react';
import { Button, Dropdown, Modal, message, Space, Alert } from 'antd';
import { SendOutlined, DownOutlined } from '@ant-design/icons';
import { apiRoot } from '../../services/apiInstances';
import { copyToClipboard } from '../../utils/clipboard';
import RelayEmailModal from './RelayEmailModal';
import { useRelayEmail } from '../../hooks/useRelayEmail';
import { useMissingRequirements } from '../../hooks/useMissingRequirements';
import MissingRequirementsChecklist from '../Estimate/MissingRequirementsChecklist';

export interface SendDialogProps {
  entityType: 'estimates' | 'work-orders' | 'invoices';
  entityId: number | undefined;
  vehicleHasVin: boolean;
  vehicleHasMileage: boolean;
  onSent?: (method: string) => void;
  /** When true the Send button is disabled (e.g. form is invalid). */
  disabled?: boolean;
  /** Precomputed customer-facing share URL. If omitted, it's resolved on demand from the
   *  estimate's public_token. Copy-link / Share items only appear for estimates. */
  shareUrl?: string;
  /** Persist current edits in place (no navigation) and resolve to the saved id.
   *  Every Send action runs this first so one press saves-then-sends without the
   *  caller having to Save (and navigate away) beforehand. */
  onSaveBeforeSend?: () => Promise<number | undefined>;
  /**
   * G2 — the Send checkpoint (spec D3).
   *
   * Supply the customer and/or vehicle record and this dialog stops using the
   * hardcoded VIN/mileage pair below and asks the SHOP POLICY instead, listing
   * every field the customer will be gated on at approval and offering a waiver
   * per field. Omit them and behaviour is exactly as before — which is what the
   * work-order and invoice callers still get.
   */
  customerId?: number | null;
  customer?: unknown;
  vehicle?: unknown;
}

const SendDialog: React.FC<SendDialogProps> = ({
  entityType,
  entityId,
  vehicleHasVin,
  vehicleHasMileage,
  onSent,
  disabled = false,
  shareUrl,
  onSaveBeforeSend,
  customerId,
  customer,
  vehicle,
}) => {
  const [warningOpen, setWarningOpen] = useState<null | { method: string }>(null);
  const { defaultEmail } = useRelayEmail();
  const [relayOpen, setRelayOpen] = useState(false);
  const [relaySending, setRelaySending] = useState(false);
  /** Fields waived from inside this dialog — dropped from the list immediately. */
  const [waived, setWaived] = useState<string[]>([]);

  /**
   * The policy path is only taken for an ESTIMATE with records to compare, because
   * the waiver endpoint is estimate-scoped and a field nobody can see the value of
   * cannot be judged. Everything else keeps the legacy two-field warning.
   */
  const policyMode = entityType === 'estimates' && !!entityId && !!(customer || vehicle);

  const { missing } = useMissingRequirements({
    customerId: customerId ?? null,
    estimateId: entityId ?? null,
    customer,
    vehicle,
    waived,
    enabled: policyMode,
  });

  // Save the in-progress estimate first (when the host wired it up), then return
  // the id to act on. Falls back to the already-persisted entityId.
  const persist = async (): Promise<number | undefined> => {
    if (onSaveBeforeSend) {
      const savedId = await onSaveBeforeSend();
      return savedId ?? entityId;
    }
    return entityId;
  };

  // Resolve the customer-facing estimate link for a saved estimate, minting a
  // public token on demand so Copy link / Share work right after a save.
  const resolveShareUrl = async (id: number): Promise<string | null> => {
    if (shareUrl) return shareUrl;
    if (entityType !== 'estimates') return null;
    try {
      const res = await apiRoot.post(`/estimates/${id}/public-link`);
      const token = (res.data as { token?: string } | undefined)?.token;
      return token ? `${window.location.origin}/estimate/${token}` : null;
    } catch {
      return null;
    }
  };

  const handleCopyLink = async () => {
    const id = await persist();
    if (!id) { message.warning('Could not save the estimate'); return; }
    const url = await resolveShareUrl(id);
    if (!url) { message.error('Could not create share link'); return; }
    const ok = await copyToClipboard(url);
    ok ? message.success('Link copied') : message.error('Could not copy link');
  };

  const handleShare = async () => {
    const id = await persist();
    if (!id) { message.warning('Could not save the estimate'); return; }
    const url = await resolveShareUrl(id);
    if (!url) { message.error('Could not create share link'); return; }
    const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
    if (typeof nav.share === 'function') {
      try {
        await nav.share({ title: 'Estimate', url });
      } catch (err: any) {
        // User dismissing the native sheet throws AbortError — not an error worth showing.
        if (err?.name !== 'AbortError') {
          const ok = await copyToClipboard(url);
          if (ok) message.success('Link copied');
        }
      }
    } else {
      const ok = await copyToClipboard(url);
      ok ? message.success('Link copied') : message.error('Could not copy link');
    }
  };

  const send = async (method: 'email' | 'sms' | 'print') => {
    // Save the current edits first, then act on the persisted estimate — so a
    // single Send press never asks the user to Save (and leave) beforehand.
    const id = await persist();
    if (!id) {
      message.warning('Could not save the estimate');
      return;
    }
    if (method === 'print') {
      window.open(`/${entityType}/${id}/print`, '_blank');
      return;
    }
    try {
      await apiRoot.post(`/${entityType}/${id}/send`, { method });
      message.success(`Sent via ${method}`);
      onSent?.(method);
    } catch (err: any) {
      message.error(
        `Send failed: ${err?.response?.data?.error ?? err?.message ?? 'unknown'}`
      );
    }
  };

  // Email this document to the owner's own inbox (default relay address, editable
  // per-send) so they can forward / text the customer link from their phone.
  const sendToSelf = async (address: string) => {
    const id = await persist();
    if (!id) { message.warning('Could not save the estimate'); return; }
    setRelaySending(true);
    try {
      await apiRoot.post(`/${entityType}/${id}/send`, { method: 'email', to: address });
      message.success('Emailed to you');
      setRelayOpen(false);
      onSent?.('email-to-me');
    } catch (err: any) {
      message.error(
        `Send failed: ${err?.response?.data?.error ?? err?.message ?? 'unknown'}`
      );
    } finally {
      setRelaySending(false);
    }
  };

  const proposeSend = (method: 'email' | 'sms') => {
    if (policyMode) {
      // The policy decides, not two hardcoded booleans. Nothing missing → straight
      // out; something missing → a decision, never a refusal.
      if (missing.length > 0) setWarningOpen({ method });
      else send(method);
      return;
    }
    if (!vehicleHasVin || !vehicleHasMileage) {
      setWarningOpen({ method });
    } else {
      send(method);
    }
  };

  const items = [
    {
      key: 'email',
      label: 'Send via Email',
      onClick: () => proposeSend('email'),
    },
    {
      key: 'sms',
      label: 'Send via SMS',
      onClick: () => proposeSend('sms'),
    },
    {
      key: 'email-to-me',
      label: 'Email to me',
      onClick: () => setRelayOpen(true),
    },
    ...(entityType === 'estimates'
      ? [
          { type: 'divider' as const },
          {
            key: 'copy-link',
            label: 'Copy estimate link',
            onClick: handleCopyLink,
          },
          {
            key: 'share',
            label: 'Share via…',
            onClick: handleShare,
          },
        ]
      : []),
    { type: 'divider' as const },
    {
      key: 'print',
      label: 'Print',
      onClick: () => send('print'),
    },
  ];

  return (
    <>
      {/* Click, not the antd default of hover: this is the primary action on a
          screen used from a phone, where there is no hover to give. */}
      <Dropdown menu={{ items }} disabled={disabled} trigger={['click']}>
        <Button type="primary" icon={<SendOutlined />} disabled={disabled}>
          Send <DownOutlined />
        </Button>
      </Dropdown>
      <RelayEmailModal
        open={relayOpen}
        defaultEmail={defaultEmail}
        sending={relaySending}
        onSend={sendToSelf}
        onClose={() => setRelayOpen(false)}
      />
      <Modal
        open={!!warningOpen}
        title={policyMode ? 'Before you send' : 'Missing vehicle info'}
        onCancel={() => setWarningOpen(null)}
        footer={
          <Space>
            <Button onClick={() => setWarningOpen(null)}>
              {policyMode ? 'Go add it' : 'Add VIN/Mileage'}
            </Button>
            <Button
              type="primary"
              onClick={() => {
                if (warningOpen) {
                  send(warningOpen.method as 'email' | 'sms');
                  setWarningOpen(null);
                }
              }}
            >
              {policyMode ? 'Send anyway' : 'Skip and Send'}
            </Button>
          </Space>
        }
      >
        {policyMode ? (
          <MissingRequirementsChecklist
            estimateId={entityId as number}
            customerId={customerId ?? null}
            missing={missing}
            onWaived={(field) => setWaived((prev) => [...prev, field])}
          />
        ) : (
          <>
            {!vehicleHasVin && (
              <Alert type="warning" message="VIN not recorded" style={{ marginBottom: 8 }} />
            )}
            {!vehicleHasMileage && <Alert type="warning" message="Mileage not recorded" />}
          </>
        )}
      </Modal>
    </>
  );
};

export default SendDialog;
